
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    Grid,
    IconButton,
    Tooltip,
    useTheme,
    alpha,
    Paper,
    Fade,
    Zoom,
    Container
} from '@mui/material';
import {
    ArrowBackIosNew as PrevIcon,
    ArrowForwardIos as NextIcon,
    WbSunny as SunIcon,
    Brightness4 as MoonIcon,
    AutoAwesome as SparkleIcon,
    InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { tamilCalendarData } from '../data/tamilCalendarData';

const TamilMonthlyCalendar = () => {
    const theme = useTheme();
    const [viewMonth, setViewMonth] = useState(10); // Thai
    const [viewYear, setViewYear] = useState(2025);
    const [selectedDate, setSelectedDate] = useState(null);

    const tamilMonthNames = [
        "", "சித்திரை", "வைகாசி", "ஆனி", "ஆடி",
        "ஆவணி", "புரட்டாசி", "ஐப்பசி", "கார்த்திகை",
        "மார்கழி", "தை", "மாசி", "பங்குனி"
    ];

    const tamilWeekDays = ["ஞாயிறு", "திங்கள்", "செவ்வாய்", "புதன்", "வியாழன்", "வெள்ளி", "சனி"];

    const currentMonthData = tamilCalendarData[`${viewMonth}_${viewYear}`] || {
        name: tamilMonthNames[viewMonth],
        year: viewYear,
        startDay: 0,
        totalDays: 30,
        days: []
    };

    const handlePrev = () => {
        if (viewMonth === 1) {
            setViewMonth(12);
            setViewYear(viewYear - 1);
        } else {
            setViewMonth(viewMonth - 1);
        }
    };

    const handleNext = () => {
        if (viewMonth === 12) {
            setViewMonth(1);
            setViewYear(viewYear + 1);
        } else {
            setViewMonth(viewMonth + 1);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh', background: 'radial-gradient(circle at 50% 0%, #f8fafc 0%, #f1f5f9 100%)' }}>
            <Fade in timeout={1000}>
                <Box>
                    {/* Top Status Bar & Nav - Enhanced with Border */}
                    <Box sx={{
                        mb: 4,
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        p: { xs: 2.5, sm: 3 },
                        borderRadius: { xs: 4, sm: 6 },
                        border: '1.5px solid rgba(0,0,0,0.08)',
                        background: 'rgba(255, 255, 255, 0.4)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 10px 30px -10px rgba(0,0,0,0.05)',
                        gap: 2
                    }}>
                        <Box>
                            <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                                Tamil Calendar
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: { xs: 1, sm: 2 } }}>
                                <Typography sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: -1, fontSize: { xs: '1.25rem', sm: '1.75rem' } }}>
                                    {currentMonthData.name}
                                </Typography>
                                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, fontWeight: 700, color: '#64748b' }}>
                                    {[...new Set(currentMonthData.days.map(d => {
                                        const names = { "Jan": "January", "Feb": "February", "Mar": "March", "Apr": "April", "May": "May", "Jun": "June", "Jul": "July", "Aug": "August", "Sep": "September", "Oct": "October", "Nov": "November", "Dec": "December" };
                                        return names[d.eMon] || d.eMon;
                                    }))].filter(Boolean).join(' - ')}
                                </Typography>
                                <Typography sx={{ color: '#94a3b8', fontWeight: 600, fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                                    {viewMonth >= 10 ? parseInt(currentMonthData.year) + 1 : currentMonthData.year}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            width: { xs: '100%', sm: 'auto' },
                            gap: 1,
                            bgcolor: 'white',
                            p: 1,
                            borderRadius: 5,
                            boxShadow: '0 4px 15px -5px rgba(0,0,0,0.05)',
                            border: '1px solid rgba(0,0,0,0.05)',
                            justifyContent: 'space-between'
                        }}>
                            <IconButton onClick={handlePrev} size="large" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), '&:hover': { bgcolor: theme.palette.primary.main, color: 'white' } }}>
                                <PrevIcon fontSize="small" />
                            </IconButton>
                            <Box sx={{ px: { xs: 2, sm: 4 }, display: 'flex', alignItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.02), borderRadius: 4, flex: 1, justifyContent: 'center' }}>
                                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', sm: '1rem' }, color: 'primary.main', whiteSpace: 'nowrap' }}>{currentMonthData.name.substring(0, 10)}</Typography>
                            </Box>
                            <IconButton onClick={handleNext} size="large" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), '&:hover': { bgcolor: theme.palette.primary.main, color: 'white' } }}>
                                <NextIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    <Card sx={{
                        borderRadius: 10,
                        overflow: 'hidden',
                        boxShadow: '0 50px 100px -20px rgba(0,0,0,0.12), 0 30px 60px -30px rgba(0,0,0,0.15)',
                        border: '1px solid white',
                        background: 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(30px)',
                    }}>
                        {/* 7-Column Header */}
                        <Grid container columns={7} sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                            {tamilWeekDays.map((day, i) => (
                                <Grid item xs={1} key={day} sx={{
                                    py: 3,
                                    textAlign: 'center',
                                    bgcolor: alpha(i === 0 || i === 6 ? '#ef4444' : theme.palette.primary.main, 0.03),
                                    flexBasis: `${100 / 7}%`,
                                    maxWidth: `${100 / 7}%`
                                }}>
                                    <Typography sx={{
                                        fontSize: '0.8rem',
                                        fontWeight: 900,
                                        color: i === 0 ? 'secondary.main' : '#475569',
                                        letterSpacing: 2,
                                        opacity: 0.8
                                    }}>
                                        {day}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Calendar Body */}
                        <Grid container columns={7}>
                            {/* Empty Padding Cells */}
                            {[...Array(currentMonthData.startDay)].map((_, i) => (
                                <Grid item xs={1} key={`pad-${i}`} sx={{
                                    flexBasis: `${100 / 7}%`,
                                    maxWidth: `${100 / 7}%`,
                                    height: { xs: 100, sm: 145 },
                                    bgcolor: 'rgba(0,0,0,0.01)',
                                    border: '0.5px solid rgba(0,0,0,0.03)'
                                }} />
                            ))}

                            {/* Date Cells */}
                            {[...Array(currentMonthData.totalDays)].map((_, i) => {
                                const date = i + 1;
                                const isWeekEnd = (date + currentMonthData.startDay - 1) % 7 === 0 || (date + currentMonthData.startDay - 1) % 7 === 6;
                                const dayData = currentMonthData.days.find(d => d.tDate === date) || {};

                                const today = new Date();
                                const isToday = today.getDate() === dayData.eDate &&
                                    (today.getMonth() + 1) === (dayData.eMon === "Jan" ? 1 : dayData.eMon === "Feb" ? 2 : dayData.eMon === "Mar" ? 3 : 0);

                                return (
                                    <Grid item xs={1} key={date} sx={{
                                        flexBasis: `${100 / 7}%`,
                                        maxWidth: `${100 / 7}%`,
                                        border: '0.5px solid rgba(0,0,0,0.05)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            zIndex: 2,
                                            boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}>
                                        <Box
                                            onClick={() => setSelectedDate(date)}
                                            sx={{
                                                p: { xs: 1.5, sm: 3 },
                                                height: { xs: 100, sm: 145 },
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                bgcolor: isToday ? alpha(theme.palette.primary.main, 0.03) : 'transparent'
                                            }}
                                        >
                                            {/* Date Header */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', zIndex: 2 }}>
                                                <Box>
                                                    <Typography sx={{
                                                        fontSize: { xs: '1.8rem', sm: '2.5rem' },
                                                        fontWeight: 950,
                                                        lineHeight: 0.9,
                                                        color: isWeekEnd ? 'secondary.main' : '#1e293b',
                                                        textShadow: isToday ? `0 0 20px ${alpha(theme.palette.secondary.main, 0.2)}` : 'none'
                                                    }}>
                                                        {date}
                                                    </Typography>
                                                    {isToday && (
                                                        <Box sx={{ mt: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: 'secondary.main', color: 'white', px: 1, py: 0.2, borderRadius: 1 }}>
                                                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, letterSpacing: 1 }}>TODAY</Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: 'secondary.main' }}>{dayData.eDate}</Typography>
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>{dayData.eMon}</Typography>
                                                </Box>
                                            </Box>

                                            {/* Panchang Info */}
                                            <Box sx={{ zIndex: 2 }}>
                                                <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#334155', mb: 0.2 }}>
                                                    {dayData.tithi}
                                                </Typography>
                                                <Typography noWrap sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', fontStyle: 'italic' }}>
                                                    {dayData.natch}
                                                </Typography>
                                            </Box>

                                            {/* Indicators */}
                                            <Box sx={{ display: 'flex', gap: 1, zIndex: 2 }}>
                                                {dayData.good && (
                                                    <Tooltip title="Subha Muhurtham">
                                                        <Box sx={{ bgcolor: alpha('#fbbf24', 0.1), p: 0.4, borderRadius: 1.5, display: 'flex' }}>
                                                            <SunIcon sx={{ fontSize: '1rem', color: '#fbbf24' }} />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                                {dayData.moon && (
                                                    <Tooltip title={dayData.moon === 'full' ? 'Pournami' : 'Amavasai'}>
                                                        <Box sx={{ bgcolor: alpha('#1e3a8a', 0.05), p: 0.4, borderRadius: 1.5, display: 'flex' }}>
                                                            <MoonIcon sx={{ fontSize: '1rem', color: dayData.moon === 'full' ? '#94a3b8' : '#1e3a8a' }} />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </Box>

                                            {/* Background Watermark */}
                                            <Typography sx={{
                                                position: 'absolute',
                                                right: -10,
                                                bottom: -15,
                                                fontSize: '6rem',
                                                fontWeight: 900,
                                                opacity: 0.02,
                                                pointerEvents: 'none',
                                                color: 'primary.main'
                                            }}>
                                                {date}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Card>

                    {/* Rich Footer Legend */}
                    <Paper sx={{
                        mt: 4,
                        p: 3,
                        borderRadius: 8,
                        bgcolor: 'white',
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 5,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                        border: '1px solid rgba(0,0,0,0.05)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <SunIcon sx={{ color: '#fbbf24' }} />
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#475569' }}>Subha Muhurtham</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <MoonIcon sx={{ color: '#1e3a8a' }} />
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#475569' }}>Lunar Cycle Landmarks</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'secondary.main' }} />
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#475569' }}>Today's Current Alignment</Typography>
                        </Box>
                    </Paper>
                </Box>
            </Fade>
        </Container>
    );
};

export default TamilMonthlyCalendar;
